import { message } from "antd";
import { i18n } from "next-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

// 复制文本
export function copyText(copytext: string, successText?: string, errorText?: string) {
  const text = document.createElement("input"); // 创建节点
  text.setAttribute("readonly", "readonly");
  text.value = copytext; // 赋值
  document.body.appendChild(text); // 插入节点
  text.setSelectionRange(0, text.value.length);
  text.select(); // 选中节点
  document.execCommand("copy"); // 执行浏览器复制方法
  if (document.body.removeChild(text)) {
    message.success(successText || "Copy success!");
  } else {
    message.error(errorText || "Copy error!");
  }
}

// 判断是否json字符串
export function isJsonString(str: string) {
  try {
    const toObj = JSON.parse(str); // json字符串转对象
    if (toObj && typeof toObj === "object") {
      return true;
    }
  } catch {}
  return false;
}

/**
 * canvas 文本换行
 * @param text 文本
 * @param x 轴
 * @param y 轴
 * @param width 行宽
 * @param fs 字体大小
 * @param rowHeight 行高，如果字体16px，1.5倍行距应该是24px
 * @param ctx 画板内容对象
 */
export function canvasWrapText(
  text: string,
  x: number,
  y: number,
  width: number,
  fs: number,
  rowHeight: number,
  ctx: any,
  style?: string
) {
  // 1 将字符串转换成数组
  let test = text.split("");
  let temp = "";
  let row: any = [];
  // // 1.1 设置样式
  ctx.font = `${fs}px 微软雅黑`;
  ctx.fillStyle = style || "black";
  ctx.textBaseline = "middle";
  // 1.2 计算文字宽度，若文字宽度大于设定的宽度，则push到数组下一个元素，否则将字符串++
  for (let i = 0; i < test.length; i++) {
    if (ctx.measureText(temp).width > width) {
      row.push(temp);
      temp = "";
    }
    temp += test[i];
  }
  // 1.3 循环结束将temp最后一段字符push
  row.push(temp);
  // 1.4 遍历数组,输出文字
  for (let j = 0; j < row.length; j++) {
    if (j < 2) {
      if (j == 1 && row.length > 2) {
        row[j] += "...";
      }
      ctx.fillText(row[j], x, y + (j + 1) * fs * rowHeight);
    }
  }
}

/**
 * 根据code码获取翻译文案
 * @param code API 返回的code码
 * @returns
 */
export async function getTranslatedTextByCode(code) {
  await i18n?.init(); // Initialize i18n
  return i18n?.t(`api.code_${code}`);
}

// 判断两个对象是否相等
export function isObjectEqual(obj1, obj2) {
  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);

  if (obj1Keys.length !== obj2Keys.length) {
    return false;
  }

  for (let key of obj1Keys) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
}

/**
 * Convert UTC time to Local time
 * @param date UTC time
 * @param format time format
 * @returns Local time
 */
export function utcToLocal(date: string | number | Date, format = "YYYY/MM/DD HH:mm:ss") {
  if (!date) return "-";
  return dayjs.utc(date).local().format(format);
}

// 欧式时间toUTC
export function europeanDateToUTC(europeanDateStr) {
  const [datePart, timePart] = europeanDateStr.split(" ");
  const [day, month, year] = datePart.split("/");
  const [hours, minutes, seconds] = timePart.split(":");
  const date = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hours),
    parseInt(minutes),
    parseInt(seconds)
  );
  return date.toISOString();
}

// 格式化USD金额
export function formatUSD(value: number = 0): string {
  value = value ?? 0; //给一个默认值，否则会出现错误
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// 截取字符串到指定长度
export function truncateToFullChar(str: string, maxLength: number) {
  return str.length > maxLength ? str.slice(0, maxLength) : str;
}

/**
 * 给 onClick 挂在 div/span 上的元素补键盘可达性。
 * 配合 role="button" tabIndex={0} 使用，回车与空格等同点击。
 * @param handler 与 onClick 相同的处理函数
 */
export function onKeyActivate<T extends { key: string; preventDefault: () => void }>(handler: () => void) {
  return (e: T) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    handler();
  };
}
